import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faList } from '@fortawesome/free-solid-svg-icons';
import billingService from '../../services/billingService';
import practiceService from '../../services/practiceService';
import { toast } from 'react-toastify';
import { formatDateOnlyUs } from '../../utils/dateOnlyUtils';
import { formatPhoneForDisplay } from '../../utils/phoneUtils';
import SearchBar from '../../components/Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import Table from '../../components/Common/Table/Table';
import StatusBadge from '../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../components/Common/ActionButtons/ActionButtons';
import EventLogSection from '../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../hooks/useEventLogModalScrollLock';
import '../../components/Common/ListScreenPageLayout/listScreenPage.css';
import '../PracticeManagement/PracticesList/PracticesList.css';
import '../PracticeManagement/SystemCaregiverManagement/SystemCaregiverManagement.css';
import './BillingDashboard.css';
import { matchesWordStart } from '../../utils/searchMatch';

function normalizePracticePrograms(programs) {
  const list = (programs || []).map((p) => String(p).toUpperCase()).filter(Boolean);
  return list.length > 0 ? list : ['RPM'];
}

function getProgramTagClass(name) {
  if (!name) return '';
  return `tag-${String(name).toLowerCase().replace(/\s+/g, '-')}`;
}

function getProgramCount(counts, programName) {
  const strKey = String(programName).toUpperCase();
  const matchKey = Object.keys(counts || {}).find((k) => k.toUpperCase() === strKey);
  const value = matchKey ? counts[matchKey] : counts?.[programName];
  if (typeof value === 'number') return value;
  if (value && typeof value === 'object') {
    if (typeof value.count === 'number') return value.count;
    if (typeof value.total === 'number') return value.total;
  }
  return 0;
}

function getPracticeDefaultProgram(practice) {
  const programs = normalizePracticePrograms(practice?.care_programs);
  return (programs[0] || 'RPM').toLowerCase();
}

const BillingDashboard = () => {
  const navigate = useNavigate();
  const { clearBreadcrumbs } = useBreadcrumb();
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLogSection, setShowLogSection] = useState(false);
  const logOverlayRef = useRef(null);
  const logContainerRef = useRef(null);
  useEventLogModalScrollLock(showLogSection, logOverlayRef, logContainerRef);

  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState('10');
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' });

  useEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  useEffect(() => {
    fetchPractices();
  }, []);

  const mapPracticeRow = (practice) => ({
    ...practice,
    billing_from_date: practice.billing_from_date || practice.billingFromDate || null,
    billing_to_date: practice.billing_to_date || practice.billingToDate || null,
    total_patients: practice.total_patients ?? practice.totalPatients ?? 0,
    active_patients: practice.active_patients ?? practice.activePatients ?? 0,
    pending_patients: practice.pending_patients ?? practice.pendingPatients ?? 0,
    locked_patients: practice.locked_patients ?? practice.lockedPatients ?? 0,
    care_programs: normalizePracticePrograms(practice.care_programs || practice.enrolled_programs),
    program_counts: practice.program_counts || {}
  });

  const fetchPractices = async () => {
    try {
      setLoading(true);
      try {
        const response = await billingService.getBillingDashboard();
        if (response.success) {
          const rows = Array.isArray(response.data) ? response.data : [];
          setPractices(rows.map(mapPracticeRow));
          return;
        }
      } catch (billingError) {
        console.warn('Billing dashboard endpoint failed, using fallback:', billingError);
      }

      const response = await practiceService.getPractices({ limit: 1000, page: 1 });
      let practicesList = [];
      if (response?.data?.success && response.data.data?.practices) {
        practicesList = response.data.data.practices;
      } else if (response?.data?.success && Array.isArray(response.data.data)) {
        practicesList = response.data.data;
      } else if (Array.isArray(response?.data)) {
        practicesList = response.data;
      }

      setPractices(practicesList.map(mapPracticeRow));
    } catch (error) {
      console.error('Error fetching practices:', error);
      toast.error('Failed to load practices');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => formatDateOnlyUs(dateString) || 'N/A';

  const formatPhone = (phone) => formatPhoneForDisplay(phone, 'N/A');

  const handleViewReport = (practice) => {
    const practiceId = typeof practice === 'object' ? practice.id : practice;
    const program = typeof practice === 'object'
      ? getPracticeDefaultProgram(practice)
      : 'rpm';
    const practiceState = typeof practice === 'object' ? practice : null;
    navigate(`/billing/manage/${practiceId}?program=${program}`, {
      state: practiceState ? { practice: practiceState } : undefined
    });
  };

  const handleViewCycleReport = (practice) => {
    const practiceId = typeof practice === 'object' ? practice.id : practice;
    const program = typeof practice === 'object'
      ? getPracticeDefaultProgram(practice)
      : 'rpm';
    const practiceState = typeof practice === 'object' ? practice : null;
    navigate(`/billing/cycle/${practiceId}?program=${program}`, {
      state: practiceState ? { practice: practiceState } : undefined
    });
  };

  const filteredPractices = practices.filter((practice) => {
    if (!searchTerm) return true;
    return matchesWordStart(practice.practice_name, searchTerm);
  });

  const getStatusOrder = (status) => {
    if (!status) return 99;
    const s = status.toString().toUpperCase();
    if (s === 'A' || s === '2' || s === 'ACTIVE') return 1;
    if (s === 'P' || s === '3' || s === 'PENDING') return 2;
    if (s === 'L' || s === '4' || s === 'LOCKED') return 3;
    return 4;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedPractices = [...filteredPractices].sort((a, b) => {
    if (!sortConfig.key) return 0;

    if (sortConfig.key === 'status') {
      const statusA = a.status || a.status_numeric || '';
      const statusB = b.status || b.status_numeric || '';
      const orderA = getStatusOrder(statusA);
      const orderB = getStatusOrder(statusB);

      if (orderA < orderB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (orderA > orderB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue == null && bValue != null) return 1;
    if (aValue != null && bValue == null) return -1;
    if (aValue == null && bValue == null) return 0;

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const pageSize = useMemo(() => {
    if (entriesPerPage === 'all') return Math.max(sortedPractices.length, 1);
    const n = parseInt(entriesPerPage, 10);
    return Number.isFinite(n) && n > 0 ? n : 10;
  }, [entriesPerPage, sortedPractices.length]);

  const totalPages = Math.max(1, Math.ceil(sortedPractices.length / pageSize) || 1);
  const startIndex = (currentPage - 1) * pageSize;
  const currentPractices =
    entriesPerPage === 'all' ? sortedPractices : sortedPractices.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  const entriesSelectValue = entriesPerPage === 'all' ? 'all' : String(entriesPerPage);

  const renderProgramEnrolled = (row) => {
    const programs = normalizePracticePrograms(row.care_programs);
    const counts = row.program_counts || {};

    return (
      <div className="program-tags-container bd-program-tags">
        {programs.map((prog) => (
          <span key={prog} className={`program-tag ${getProgramTagClass(prog)}`}>
            {prog} ({getProgramCount(counts, prog)})
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="billing-dashboard-feature">
      <div className="list-screen-page-container scm-page bd-page-root">
        <div className="scm-page-header">
          <h2 className="dashboard-title">Billing</h2>
        </div>
        <div className="scm-list-frame">
          <div className="scm-list-panel">
            <div className="scm-list-body">
              <div className="scm-header-row">
                <div className="scm-header-actions">
                  <SearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search practices..."
                    label={null}
                    className="scm-search-wrapper"
                  />
                </div>
              </div>

              <div className="practice-tab-table-card bd-table-container">
                <Table
                  columns={[
                    { key: 'id', title: 'ID', sortable: true, width: '4%', className: 'reusable-table-centered-col bd-col-id', render: (row) => row.id },
                    {
                      key: 'status',
                      title: 'Status',
                      sortable: true,
                      width: '5%',
                      className: 'reusable-table-centered-col bd-col-status',
                      render: (row) => <StatusBadge status={(row.status || row.status_numeric || 'N/A').toString()} />
                    },
                    {
                      key: 'practice_name',
                      title: 'Name',
                      sortable: true,
                      width: '15%',
                      className: 'bd-col-name',
                      render: (row) => (
                        <button
                          type="button"
                          className="practices-list-name-link bd-practice-name-link"
                          onClick={() => handleViewReport(row)}
                        >
                          {row.practice_name || 'N/A'}
                        </button>
                      )
                    },
                    { key: 'phone_1', title: 'Phone 1', width: '10%', className: 'bd-col-phone', render: (row) => formatPhone(row.phone_1) },
                    { key: 'email_1', title: 'Email', width: '17%', className: 'bd-col-email', render: (row) => (
                      <span className="bd-cell-ellipsis" title={row.email_1 || ''}>{row.email_1 || 'N/A'}</span>
                    ) },
                    {
                      key: 'care_programs',
                      title: 'Program Enrolled',
                      sortable: true,
                      width: '16%',
                      className: 'reusable-table-centered-col bd-col-program',
                      render: renderProgramEnrolled
                    },
                    {
                      key: 'billingPeriod',
                      title: 'Billing Period Date',
                      width: '15%',
                      className: 'bd-col-period',
                      render: (row) =>
                        row.billing_from_date && row.billing_to_date
                          ? `${formatDate(row.billing_from_date)} ~ ${formatDate(row.billing_to_date)}`
                          : 'N/A'
                    },
                    {
                      key: 'patientsStatus',
                      title: 'Patients Status',
                      width: '13%',
                      className: 'reusable-table-centered-col bd-col-patients',
                      render: (row) => (
                        <div className="bd-patients-status">
                          <span className="bd-patient-pill total">T {row.total_patients || 0}</span>
                          <span className="bd-patient-pill active">A {row.active_patients || 0}</span>
                          <span className="bd-patient-pill pending">P {row.pending_patients || 0}</span>
                          <span className="bd-patient-pill lost">L {row.locked_patients || 0}</span>
                        </div>
                      )
                    },
                    {
                      key: 'view',
                      title: 'View Report',
                      width: '5%',
                      className: 'reusable-table-centered-col bd-col-actions',
                      render: (row) => (
                        <ActionButtons
                          onView={() => handleViewReport(row)}
                          hideEdit
                          hideDelete
                          extraButtons={() => (
                            <button
                              type="button"
                              className="res-action-btn bd-calendar-btn"
                              onClick={() => handleViewCycleReport(row)}
                              title="Billing Cycle Report"
                            >
                              <FontAwesomeIcon icon={faCalendarAlt} />
                            </button>
                          )}
                        />
                      )
                    }
                  ]}
                  data={currentPractices}
                  sortBy={sortConfig.key}
                  sortOrder={sortConfig.direction}
                  onSort={handleSort}
                  isLoading={loading}
                  loadingMode="skeleton"
                  skeletonRows={10}
                  emptyMessage="No practices found"
                />

                <div className="scm-list-footer">
                  <ListScreenPaginationBar
                    entriesValue={entriesSelectValue}
                    onEntriesChange={(value) => {
                      setEntriesPerPage(value === 'all' ? 'all' : value);
                      setCurrentPage(1);
                    }}
                    showPageSelector={sortedPractices.length > 0}
                    currentPage={currentPage}
                    totalRecords={sortedPractices.length}
                    totalPages={Math.max(totalPages, 1)}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bd-event-log-actions">
          <button
            type="button"
            className="scm-events-log-btn"
            onClick={() => setShowLogSection(true)}
          >
            <FontAwesomeIcon icon={faList} className="list-screen-me-2" />
            Event Logs
          </button>
        </div>

        {showLogSection && (
          <div
            ref={logOverlayRef}
            className="practices-list-modal-overlay"
            role="presentation"
          >
            <div
              ref={logContainerRef}
              className="practices-list-modal-container"
              style={{ maxWidth: '900px', width: '90%' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="billing-dashboard-event-logs-title"
            >
              <div className="practices-list-modal-header">
                <h5 id="billing-dashboard-event-logs-title" className="practices-list-modal-title">
                  Billing Event Logs
                </h5>
                <button
                  type="button"
                  className="practices-list-modal-close"
                  onClick={() => setShowLogSection(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <EventLogSection moduleId="Billing" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default BillingDashboard;
